import DOMPurify from 'dompurify';

/**
 * The one sanitiser for every piece of HTML this app injects into the page.
 *
 * Rich text reaches us from two places we do not control character by character:
 * product `description` / `short_description`, authored in the admin, and gift
 * card `terms` / `redemption_instructions`, authored upstream by
 * Razorpay/Poshvine. Both end up in `dangerouslySetInnerHTML`, so both are one
 * bad string away from running script in a logged-in customer's session — and
 * the session token lives in localStorage, which any injected script can read.
 *
 * This replaces a hand-written regex sanitiser. Regexes cannot parse HTML, and
 * that one demonstrably could not: of nine standard payloads, four survived it —
 * `<img/onerror=…>` and `<svg/onload=…>` (the handler was preceded by a slash
 * rather than the whitespace the pattern required), `<a href=javascript:…>`
 * unquoted (the pattern only matched quoted URLs), and `<body/onload=…>`. A
 * `<form action="https://evil.tld">` passed through untouched, which is a
 * payment form on a product page.
 *
 * DOMPurify parses the markup the way the browser will and keeps an allowlist,
 * so a payload shaped in a way nobody here thought of is still dropped.
 */

// Formatting only. Everything that can submit, load or execute is absent by
// construction rather than removed by rule — that is the point of an allowlist.
const ALLOWED_TAGS = [
  'p', 'br', 'hr', 'span', 'div',
  'b', 'strong', 'i', 'em', 'u', 's', 'small', 'sub', 'sup', 'mark',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'blockquote', 'pre', 'code',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  'a', 'img', 'figure', 'figcaption',
];

const ALLOWED_ATTR = [
  'href', 'title', 'alt', 'src', 'width', 'height',
  'colspan', 'rowspan', 'align', 'class', 'target', 'rel',
];

/**
 * Injected links open in a new tab without handing it a reference back to us.
 * Registered once — DOMPurify hooks are global, and adding one per call would
 * stack a new hook on every render.
 */
let hookAdded = false;
function ensureHook() {
  if (hookAdded || typeof window === 'undefined') return;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.hasAttribute('href')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer nofollow');
    }
  });
  hookAdded = true;
}

/**
 * @param {string} html untrusted markup
 * @returns {string} markup safe to hand to dangerouslySetInnerHTML
 */
export function sanitizeHtml(html) {
  if (!html) return '';
  ensureHook();
  return DOMPurify.sanitize(String(html), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // `data:` and `javascript:` URLs never survive; http(s), mailto and tel do.
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    // A sanitised fragment must not be able to break out of its container.
    FORBID_TAGS: ['form', 'input', 'button', 'select', 'textarea', 'style', 'base', 'link'],
    FORBID_ATTR: ['style', 'formaction', 'action', 'srcdoc', 'ping'],
    USE_PROFILES: { html: true },
  });
}

/**
 * Kept as a named alias because the catalogue components have always called it
 * this. Same function — there is only one sanitiser now.
 */
export const sanitizeCatalogueHtml = sanitizeHtml;
